"use client";

import { themeColors } from "@/utils/themeColors";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import toast from "react-hot-toast";
import { CopyIcon } from "lucide-react";
import { usePublicProfiles } from "@knowt/syncing/hooks/user/useUserData";
import { UserProfileCardWithFollowHandler } from "../../../../components/userListCard/UserProfileCard";
import React from "react";
import { User } from "@knowt/syncing/graphql/schema";
import { BASE_URL } from "@/config/deployConstants";
import clsx from "clsx";
import Mountable from "@/components/styled/div/Mountable";

type Props = {
    id?: string;
    referral?: string | null;
    isOwner?: boolean;
    refList?: string[];
    onQRPopupOpen?: () => void;
    style?: React.CSSProperties;
    className?: string;
};

const LinkBox = ({ referral, onQRPopupOpen }: { referral?: string | null; onQRPopupOpen?: () => void }) => {
    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Failed to copy to clipboard");
        }
    };

    const handleCodeClick = () => {
        handleCopy(referral || "");
        onQRPopupOpen?.();
    };

    const inviteLink = `${BASE_URL}/invite/${referral || ""}`;

    return (
        <FlexColumn
            style={{
                width: "100%",
            }}>
            <FlexRowAlignCenter
                onClick={() => handleCopy(inviteLink)}
                style={{
                    backgroundColor: themeColors.background,
                    borderRadius: `${spacing.LG}`,
                    padding: `${spacing.XS_2} ${spacing.MD}`,
                    cursor: "pointer",
                    marginBottom: `${spacing.MD}`,
                    justifyContent: "space-between",
                    gap: `${spacing.XS_2}`,
                }}>
                <span
                    className={clsx("tertiaryText1", "clampText")}
                    style={{
                        WebkitLineClamp: 1,
                        color: themeColors.neutral3,
                        overflow: "hidden",
                    }}
                    data-testid="auto-id-b463cd6a-978c-4468-847a-77b8e93302cb">
                    {inviteLink}
                </span>
                <CopyIcon size={18} style={{ flexShrink: 0 }} color={themeColors.neutralBlack} />
            </FlexRowAlignCenter>
            <FlexRowAlignCenter
                onClick={handleCodeClick}
                style={{
                    backgroundColor: themeColors.background,
                    borderRadius: `${spacing.LG}`,
                    padding: `${spacing.XS_2} ${spacing.MD}`,
                    cursor: "pointer",
                    marginBottom: `${spacing.MD}`,
                    justifyContent: "space-between",
                    gap: `${spacing.XS_2}`,
                }}>
                <span
                    className={clsx("tertiaryText1", "clampText")}
                    style={{ color: themeColors.neutral3 }}
                    data-testid="auto-id-b30399d4-3e69-43bf-90ee-f84cc724a83d">
                    {referral}
                </span>
                <CopyIcon size={18} style={{ flexShrink: 0 }} color={themeColors.neutralBlack} />
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};

const UserProfileInviteLink = ({ id, referral, isOwner, refList, onQRPopupOpen, style, className }: Props) => {
    const { usersData } = usePublicProfiles(refList);

    return (
        <FlexColumn id={id} className={className} style={{ gap: `${spacing.MD}`, justifyContent: "center", ...style }}>
            <FlexColumn style={{ alignItems: "center", gap: "inherit" }}>
                {isOwner ? (
                    <p
                        className="secondaryTextBold1"
                        style={{ alignSelf: "flex-start" }}
                        data-testid="auto-id-62983300-8c74-4e96-89be-fe3494c82df3">
                        Invite Link & Code
                    </p>
                ) : (
                    <FlexColumn>
                        <p className="bodyBold1" data-testid="auto-id-4c63bf33-2eeb-4d0b-ad7b-1d22ae07b70c">
                            They have no followers!
                        </p>
                        <p className="body1" data-testid="auto-id-e1388e43-acae-4f5d-8e7c-82ed29b4d1ae">
                            Start following them and they{" "}
                            <br data-testid="auto-id-ec7405aa-7acc-4ee4-8030-f2af2b22e7cc" /> might follow you back 😉
                        </p>
                    </FlexColumn>
                )}

                <Mountable mount={!!isOwner}>
                    <LinkBox referral={referral} onQRPopupOpen={onQRPopupOpen} />
                </Mountable>
            </FlexColumn>
            <Mountable mount={!!isOwner && !!refList?.length}>
                <FlexColumn>
                    <p
                        className="bodyBold1"
                        style={{ alignSelf: "flex-start", marginBottom: `${spacing.XS_2}` }}
                        data-testid="auto-id-3e273d85-3bd3-4589-b6da-94a6f2328b5c">
                        Referred ({refList?.length || 0})
                    </p>
                    <FlexColumn style={{ gap: spacing.XS }}>
                        {Object.values(usersData)?.map(user => (
                            <UserProfileCardWithFollowHandler user={user as unknown as User} key={user.ID} />
                        ))}
                    </FlexColumn>
                </FlexColumn>
            </Mountable>
        </FlexColumn>
    );
};

export default UserProfileInviteLink;
